import { useAdminMutation } from '@/hooks/admin/useAdminQuery'
import { useDefinedQuery } from '@/hooks/useDefinedQuery'
import type { QueryDef } from '@/hooks/useDefinedQuery'
import { api } from '@/services/api'
import {
  getAdminStats,
  getAdminJobsOverview,
  getAdminFailedJobs,
  getAdminRecurringTasks,
  getAdminScheduledJobs,
  getAdminPendingJobs,
  getAdminProcessingJobs,
  getAdminTriggerableJobs,
  getAdminCompletedJobs,
  getAdminLogs,
  getAdminSessions,
  getAdminApiUsage,
} from '@/services/api/admin'
import { getAdminFeatures } from '@/services/api/features'

const STALE_30S = 30_000

const adminQueries = {
  stats: {
    queryKey: () => ['admin', 'stats'] as const,
    queryFn: getAdminStats,
    staleTime: STALE_30S,
    auth: 'admin',
  } satisfies QueryDef<Awaited<ReturnType<typeof getAdminStats>>>,
  jobsOverview: {
    queryKey: () => ['admin', 'jobs', 'overview'] as const,
    queryFn: getAdminJobsOverview,
    staleTime: STALE_30S,
    auth: 'admin',
  } satisfies QueryDef<Awaited<ReturnType<typeof getAdminJobsOverview>>>,
  failedJobs: {
    queryKey: () => ['admin', 'jobs', 'failed'] as const,
    queryFn: getAdminFailedJobs,
    staleTime: STALE_30S,
    auth: 'admin',
  } satisfies QueryDef<Awaited<ReturnType<typeof getAdminFailedJobs>>>,
  recurringTasks: {
    queryKey: () => ['admin', 'jobs', 'recurring'] as const,
    queryFn: getAdminRecurringTasks,
    staleTime: STALE_30S,
    auth: 'admin',
  } satisfies QueryDef<Awaited<ReturnType<typeof getAdminRecurringTasks>>>,
  scheduledJobs: {
    queryKey: () => ['admin', 'jobs', 'scheduled'] as const,
    queryFn: getAdminScheduledJobs,
    staleTime: STALE_30S,
    auth: 'admin',
  } satisfies QueryDef<Awaited<ReturnType<typeof getAdminScheduledJobs>>>,
  pendingJobs: {
    queryKey: () => ['admin', 'jobs', 'pending'] as const,
    queryFn: getAdminPendingJobs,
    staleTime: STALE_30S,
    auth: 'admin',
  } satisfies QueryDef<Awaited<ReturnType<typeof getAdminPendingJobs>>>,
  processingJobs: {
    queryKey: () => ['admin', 'jobs', 'processing'] as const,
    queryFn: getAdminProcessingJobs,
    staleTime: STALE_30S,
    auth: 'admin',
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
  } satisfies QueryDef<Awaited<ReturnType<typeof getAdminProcessingJobs>>>,
  completedJobs: {
    queryKey: () => ['admin', 'jobs', 'completed'] as const,
    queryFn: () => getAdminCompletedJobs(),
    staleTime: STALE_30S,
    auth: 'admin',
  } satisfies QueryDef<Awaited<ReturnType<typeof getAdminCompletedJobs>>>,
  triggerableJobs: {
    queryKey: () => ['admin', 'jobs', 'triggerable'] as const,
    queryFn: getAdminTriggerableJobs,
    staleTime: STALE_30S,
    auth: 'admin',
  } satisfies QueryDef<Awaited<ReturnType<typeof getAdminTriggerableJobs>>>,
  logs: {
    queryKey: (limit: number) => ['admin', 'logs', limit] as const,
    queryFn: (limit: number) => getAdminLogs(limit),
    staleTime: STALE_30S,
    auth: 'admin',
    refetchInterval: false as const,
  } satisfies QueryDef<Awaited<ReturnType<typeof getAdminLogs>>, [number]>,
  sessions: {
    queryKey: () => ['admin', 'sessions'] as const,
    queryFn: getAdminSessions,
    staleTime: STALE_30S,
    auth: 'admin',
  } satisfies QueryDef<Awaited<ReturnType<typeof getAdminSessions>>>,
  apiUsage: {
    queryKey: () => ['admin', 'api_usage'] as const,
    queryFn: getAdminApiUsage,
    staleTime: STALE_30S,
    auth: 'admin',
  } satisfies QueryDef<Awaited<ReturnType<typeof getAdminApiUsage>>>,
  features: {
    queryKey: () => ['admin', 'features'] as const,
    queryFn: getAdminFeatures,
    staleTime: STALE_30S,
    auth: 'admin',
  } satisfies QueryDef<Awaited<ReturnType<typeof getAdminFeatures>>>,
}

// --- Queries ---

export function useAdminStats() {
  return useDefinedQuery(adminQueries.stats)
}

export function useAdminJobsOverview() {
  return useDefinedQuery(adminQueries.jobsOverview)
}

export function useAdminFailedJobs() {
  return useDefinedQuery(adminQueries.failedJobs)
}

export function useAdminRecurringTasks() {
  return useDefinedQuery(adminQueries.recurringTasks)
}

export function useAdminScheduledJobs() {
  return useDefinedQuery(adminQueries.scheduledJobs)
}

export function useAdminPendingJobs() {
  return useDefinedQuery(adminQueries.pendingJobs)
}

export function useAdminProcessingJobs() {
  return useDefinedQuery(adminQueries.processingJobs)
}

export function useAdminCompletedJobs() {
  return useDefinedQuery(adminQueries.completedJobs)
}

export function useAdminTriggerableJobs() {
  return useDefinedQuery(adminQueries.triggerableJobs)
}

export function useAdminLogs(limit = 100) {
  return useDefinedQuery(adminQueries.logs, limit)
}

export function useAdminSessions() {
  return useDefinedQuery(adminQueries.sessions)
}

export function useAdminApiUsage() {
  return useDefinedQuery(adminQueries.apiUsage)
}

// --- Feature flag queries ---

export function useAdminFeatures() {
  return useDefinedQuery(adminQueries.features)
}

// --- Job mutations ---

export function useRetryAdminJob() {
  return useAdminMutation((id: number) => api.retryAdminJob(id), 'adminJobs')
}

export function useDiscardAdminJob() {
  return useAdminMutation((id: number) => api.discardAdminJob(id), 'adminJobs')
}

export function useCancelAdminScheduledJob() {
  return useAdminMutation((id: number) => api.cancelAdminScheduledJob(id), 'adminJobs')
}

export function useCancelAllAdminScheduledJobs() {
  return useAdminMutation((jobClass?: string) => api.cancelAllAdminScheduledJobs(jobClass), 'adminJobs')
}

export function useCancelAdminPendingJob() {
  return useAdminMutation((id: number) => api.cancelAdminPendingJob(id), 'adminJobs')
}

export function useCancelAllAdminPendingJobs() {
  return useAdminMutation((jobClass: string) => api.cancelAllAdminPendingJobs(jobClass), 'adminJobs')
}

export function useTriggerAdminJob() {
  return useAdminMutation((jobClass: string) => api.triggerAdminJob(jobClass), 'adminJobs')
}

export function usePauseAdminQueue() {
  return useAdminMutation((queueName: string) => api.pauseAdminQueue(queueName), 'adminJobs')
}

export function useResumeAdminQueue() {
  return useAdminMutation((queueName: string) => api.resumeAdminQueue(queueName), 'adminJobs')
}

// --- Session mutations ---

export function useRevokeAdminSession() {
  return useAdminMutation((id: number) => api.revokeAdminSession(id), 'adminSessions')
}

export function useRevokeUserSessions() {
  return useAdminMutation((userId: number) => api.revokeUserSessions(userId), 'adminSessions')
}

// --- Feature flag mutations ---

export function useCreateAdminFeature() {
  return useAdminMutation((key: string) => api.createAdminFeature(key), 'adminFeatures')
}

export function useUpdateAdminFeature() {
  return useAdminMutation(
    (params: { key: string; actionType: 'enable' | 'disable'; targetType?: string; targetValue?: string | number }) =>
      api.updateAdminFeature(params.key, params.actionType, params.targetType, params.targetValue),
    'adminFeatures',
  )
}

export function useDeleteAdminFeature() {
  return useAdminMutation((key: string) => api.deleteAdminFeature(key), 'adminFeatures')
}
